import Hammer from 'hammerjs';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { DOC_TYPE_ALIAS } from '../../../config/helpers';

import Icon from '../Atoms/Icon';
import ErrorBoundary from '../Core/ErrorBoundary';
import FeaturedPackage from './FeaturedPackage';
import FeaturedPage from './FeaturedPage';
import {
  currencyCodeAndSymbolPropTypes,
  findCurrencyItemFromListWithFallback,
} from '../../../util/currency';

const propTypes = exact({
  autoplay: PropTypes.bool.isRequired,
  currentCurrency: currencyCodeAndSymbolPropTypes,
  fromPriceText: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  interval: PropTypes.number.isRequired,
  limitedAvailabilityText: PropTypes.string.isRequired,
  perPersonText: PropTypes.string,
  sideButtons: PropTypes.bool,
  soldOutText: PropTypes.string.isRequired,
  viewTourText: PropTypes.string,
});

const defaultProps = {
  fromPriceText: 'from',
  perPersonText: 'pp',
  sideButtons: false,
  viewTourText: 'view package',
};

class FeatureCarousel extends Component {
  constructor(props) {
    super(props);

    this.carousel = null;
    this.touchManager = null;

    this.addTouchSwipe = this.addTouchSwipe.bind(this);
    this.autoplay = this.autoplay.bind(this);
    this.handleSwipeLeft = this.handleSwipeLeft.bind(this);
    this.handleSwipeRight = this.handleSwipeRight.bind(this);

    this.state = { activeSlideIndex: 0 };
  }

  componentDidMount() {
    setTimeout(this.addTouchSwipe, 0);
    if (this.props.autoplay) this.autoplay();
  }

  componentWillUnmount() {
    if (this.touchManager) {
      this.touchManager.off('swipeleft');
      this.touchManager.off('swiperight');
    }

    if (this.autoplayInterval) clearInterval(this.autoplayInterval);
  }

  autoplay() {
    this.autoplayInterval = setInterval(() => {
      let requestedIndex = this.state.activeSlideIndex + 1;
      if (requestedIndex > this.props.items.length - 1) requestedIndex = 0;
      this.handleSlideRequest(requestedIndex, true);
    }, this.props.interval * 1000);
  }

  addTouchSwipe() {
    if (!this.carousel) return;

    this.touchManager = new Hammer.Manager(this.carousel, { touchAction: 'pan-y' });
    const swipe = new Hammer.Swipe();

    this.touchManager.add(swipe);
    this.touchManager.on('swipeleft', this.handleSwipeLeft);
    this.touchManager.on('swiperight', this.handleSwipeRight);
  }

  handleSwipeLeft() {
    if (this.state.activeSlideIndex >= this.props.items.length - 1) return;
    this.handleSlideRequest(this.state.activeSlideIndex + 1);
  }

  handleSwipeRight() {
    if (this.state.activeSlideIndex <= 0) return;
    this.handleSlideRequest(this.state.activeSlideIndex - 1);
  }

  handleSlideRequest(index, autoplay) {
    this.setState({ activeSlideIndex: index });
    if (!autoplay) clearInterval(this.autoplayInterval);
  }

  render() {
    const items = this.props.items.map((item, index) => {
      const itemStatus = item;
      if (index === this.state.activeSlideIndex) itemStatus.status = 'active';
      else if (index > this.state.activeSlideIndex) itemStatus.status = 'next';
      else if (index < this.state.activeSlideIndex) itemStatus.status = 'previous';
      return itemStatus;
    });

    return (
      <ErrorBoundary>
        <div
          className="FeatureCarousel"
          ref={(element) => {
            this.carousel = element;
          }}
        >
          <ul className="FeatureCarousel_slider">
            {items.map((item, index) => {
              const priceInCurrentCurrency = findCurrencyItemFromListWithFallback(
                item.pricePerCurrency,
                this.props.currentCurrency?.code
              )?.price;
              return (
                <li key={item.id} className={`FeatureCarousel_slide ${item.status}`}>
                  {item.documentTypeAlias === DOC_TYPE_ALIAS.TOUR && (
                    <FeaturedPackage
                      currentCurrency={this.props.currentCurrency}
                      index={index}
                      date={item.date}
                      description={item.description}
                      fromPriceText={this.props.fromPriceText}
                      image={item.image}
                      inclusions={item.inclusions}
                      isSoldOut={item.isSoldOut}
                      perPersonText={this.props.perPersonText}
                      price={priceInCurrentCurrency}
                      soldOutText={this.props.soldOutText}
                      status={item.status}
                      title={item.title}
                      tournamentName={item.tournamentName}
                      url={item.url}
                      limitedAvailability={item.limitedAvailability}
                      limitedAvailabilityText={this.props.limitedAvailabilityText}
                      limitedAvailabilityTextOverride={item.limitedAvailabilityText}
                      limitedAvailabilityRemainingSpaces={item.limitedAvailabilityRemainingSpaces}
                      viewTourText={this.props.viewTourText}
                    />
                  )}
                  {item.documentTypeAlias !== DOC_TYPE_ALIAS.TOUR && (
                    <FeaturedPage
                      index={index}
                      description={item.description}
                      documentTypeAlias={item.documentTypeAlias}
                      image={item.image}
                      status={item.status}
                      title={item.title}
                      url={item.url}
                    />
                  )}
                </li>
              );
            })}
          </ul>
          {items.length > 1 && (
            <>
              <ul className="FeatureCarousel_navigationList">
                {items.map((item, index) => (
                  <li key={item.id} className="FeatureCarousel_navigationItem">
                    <button
                      type="button"
                      onClick={() => this.handleSlideRequest(index)}
                      className={`FeatureCarousel_navigationButton ${item.status}`}
                      aria-label={`FeatureCarousel Slide ${index + 1}`}
                    />
                  </li>
                ))}
              </ul>
              {this.props.sideButtons && (
                <div className="FeatureCarousel_sideButtons">
                  <button
                    type="button"
                    className={`FeatureCarousel_leftButton${
                      this.state.activeSlideIndex === 0 ? ' FeatureCarousel_leftButton-hidden' : ''
                    }`}
                    onClick={() => this.handleSwipeRight()}
                  >
                    <Icon id="arrow-left" />
                  </button>
                  <button
                    type="button"
                    className={`FeatureCarousel_rightButton${
                      this.state.activeSlideIndex === items.length - 1
                        ? ' FeatureCarousel_rightButton-hidden'
                        : ''
                    }`}
                    onClick={() => this.handleSwipeLeft()}
                  >
                    <Icon id="arrow-right" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </ErrorBoundary>
    );
  }
}

FeatureCarousel.propTypes = propTypes;
FeatureCarousel.defaultProps = defaultProps;

export default FeatureCarousel;
